/* Buon Cibo wine wall.
 *
 * Reads assets/bc-wine-wall.json, which is generated from data/wine/wines-audited-108.json by
 * scratchpad/gen_theme_wine_data.py. Never hand-edit the JSON.
 *
 * Three things this file is careful about:
 *
 *   1. The audit gate. Every record can carry `_uncertified`, the fields the 2026-09-16 wine audit
 *      has not cleared for publication. When the section switch is off those fields are stripped
 *      before anything renders, so the page cannot show an uncertified claim by accident.
 *   2. The dialog is a native <dialog>. That gives the focus trap, the Escape key and the backdrop
 *      for free; all this file adds is returning focus to the card that opened it.
 *   3. One input does the whole search. A number is a wall number and jumps straight to the bottle.
 *      Anything else filters. Design system section 10 wants search by number, producer, grape,
 *      region or dish visible - that is one field, not five.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-bc-wine-wall]');
  if (!root) return;

  var SHOW_UNCERTIFIED = root.getAttribute('data-show-uncertified') === 'true';
  var DATA_URL = root.getAttribute('data-wine-data-url');

  var grid = root.querySelector('[data-bc-ww-grid]');
  var chips = root.querySelector('[data-bc-ww-chips]');
  var countEl = root.querySelector('[data-bc-ww-count]');
  var bars = root.querySelector('[data-bc-ww-bars]');
  var input = root.querySelector('[data-bc-ww-search]');
  var hint = root.querySelector('[data-bc-ww-hint]');
  var sheet = root.querySelector('[data-bc-ww-sheet]');
  var sheetIn = root.querySelector('[data-bc-ww-sheet-in]');

  var WINES = [];
  var type = 'all';
  var query = '';
  var opener = null;

  var TYPES = [
    ['all', 'All'], ['red', 'Red'], ['white', 'White'], ['sparkling', 'Sparkling'],
    ['rose', 'Rosé'], ['orange', 'Orange'], ['zero_alcohol', 'Zero alcohol'], ['canned', 'Cans']
  ];

  /* ---------------------------------------------------------------- helpers */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(n) {
    if (n == null) return '';
    return '$' + (Number(n) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2));
  }

  function readable(v) {
    if (v == null || v === '') return '';
    return String(v).replace(/_/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); });
  }

  function list(v) {
    if (!v) return [];
    return (Array.isArray(v) ? v : [v]).filter(Boolean);
  }

  /* The audit gate. With the switch off, a field named in `_uncertified` never reaches the DOM. */
  function certify(w) {
    if (SHOW_UNCERTIFIED || !w._uncertified) return w;
    var out = {};
    var blocked = {};
    w._uncertified.forEach(function (f) { blocked[f] = true; });
    Object.keys(w).forEach(function (k) { if (!blocked[k]) out[k] = w[k]; });
    return out;
  }

  function metaLine(w) {
    return [w.vintage, w.grape_primary, w.region || w.country].filter(Boolean).join(' · ');
  }

  function tagHtml(w) {
    if (w.wall_number) {
      return '<span class="bc-ww__tag"><span class="bc-ww__tag-lab">Wall</span>' +
        '<span class="bc-ww__tag-num">' + esc(w.wall_number) + '</span></span>';
    }
    return '<span class="bc-ww__tag bc-ww__tag--mkt"><span class="bc-ww__tag-lab">Market</span>' +
      '<span class="bc-ww__tag-num">Pick</span></span>';
  }

  /* ---------------------------------------------------------------- the card */

  function cardHtml(w) {
    var chipline = [readable(w.category), w.body ? readable(w.body) + ' body' : '']
      .filter(Boolean)
      .map(function (c) { return '<em>' + esc(c) + '</em>'; }).join('');
    var summary = w.beginner_description || w.style_summary || '';
    var pair = w.best_buon_cibo_pairing;

    return '<button class="bc-ww__card" type="button" data-id="' + esc(w.wine_id) + '" ' +
      'aria-label="' + esc(w.display_name) + ', details">' +
      tagHtml(w) +
      '<span class="bc-ww__card-body">' +
        '<span class="bc-ww__card-name">' + esc(w.display_name) + '</span>' +
        '<span class="bc-ww__card-meta">' + esc(metaLine(w)) + '</span>' +
        (chipline ? '<span class="bc-ww__chipline">' + chipline + '</span>' : '') +
        (summary ? '<span class="bc-ww__card-meta">' + esc(summary) + '</span>' : '') +
        (pair ? '<span class="bc-ww__card-pair"><b>Good with</b>' + esc(pair) + '</span>' : '') +
      '</span>' +
      '<span class="bc-ww__card-price">' + money(w.price) + '</span>' +
    '</button>';
  }

  /* ---------------------------------------------------------------- the dialog */

  var SCALES = [
    ['Body', 'body', { light: 1, light_medium: 2, medium: 3, full: 4 }],
    ['Acidity', 'acidity', { low: 1, medium: 2, medium_high: 3, high: 4 }],
    ['Tannin', 'tannin', { none: 0, low: 1, medium: 2, medium_high: 3, high: 4 }],
    ['Oak', 'oak_level', { none: 0, low: 1, medium: 2, medium_high: 3, high: 4 }],
    ['Sweetness', 'sweetness', { bone_dry: 0, dry: 1, off_dry: 2, sweet: 3 }]
  ];

  function scalesHtml(w) {
    var rows = SCALES.map(function (r) {
      if (w[r[1]] == null) return '';
      var v = r[2][w[r[1]]];
      if (v == null) return '';
      var pips = '';
      for (var i = 1; i <= 4; i++) pips += '<i class="' + (i <= v ? 'is-on' : '') + '"></i>';
      return '<div class="bc-ww__scale"><b>' + r[0] + '</b>' +
        '<span class="bc-ww__pips">' + pips + '</span>' +
        '<u>' + esc(readable(w[r[1]])) + '</u></div>';
    }).join('');
    return rows
      ? '<div class="bc-ww__blk"><h4>Taste</h4><div class="bc-ww__scales">' + rows + '</div></div>'
      : '';
  }

  function serveHtml(w) {
    var out = [];
    if (w.chill_minutes) out.push('<div><b>Chill</b><span>' + esc(w.chill_minutes) + ' min</span></div>');
    if (w.decant_minutes) out.push('<div><b>Open early</b><span>' + esc(w.decant_minutes) + ' min</span></div>');
    if (w.alcohol_percent) out.push('<div><b>Alcohol</b><span>' + esc(w.alcohol_percent) + '%</span></div>');
    if (!out.length) return '';
    return '<div class="bc-ww__blk"><h4>Pouring it</h4><div class="bc-ww__serve">' +
      out.join('') + '</div></div>';
  }

  function tagsHtml(label, values, cls) {
    var l = list(values);
    if (!l.length) return '';
    return '<div class="bc-ww__blk"><h4>' + label + '</h4>' +
      '<div class="bc-ww__tags ' + (cls || '') + '">' +
      l.map(function (t) { return '<span>' + esc(t) + '</span>'; }).join('') +
      '</div></div>';
  }

  function blk(label, text, cls) {
    if (!text) return '';
    return '<div class="bc-ww__blk"><h4>' + label + '</h4>' +
      '<p class="' + (cls || '') + '">' + esc(text) + '</p></div>';
  }

  function sheetHtml(w) {
    var origin = [
      ['Grapes', w.grape_blend || w.grape_primary],
      ['Appellation', w.appellation],
      ['Region', w.subregion || w.region],
      ['Country', w.country],
      ['Classification', w.classification],
      ['Aged in', w.aging_vessel],
      ['For', w.aging_length],
      ['Drink', readable(w.drinking_window)],
      ['Size', (w.bottle_size_ml || 750) + ' ml']
    ].filter(function (r) { return r[1]; });

    return '<div class="bc-ww__sheet-top">' + tagHtml(w) +
        '<span class="bc-ww__sheet-name">' +
          '<strong>' + esc(w.display_name) + '</strong>' +
          '<span>' + esc(metaLine(w)) + '</span>' +
        '</span>' +
        '<span class="bc-ww__sheet-price">' + money(w.price) + '</span>' +
        '<button class="bc-ww__sheet-x" type="button" data-bc-ww-close aria-label="Close">' +
          '&#10005;</button>' +
      '</div>' +
      '<div class="bc-ww__sheet-body">' +
        '<div class="bc-ww__blk bc-ww__blk-col">' +
          blk('In plain English', w.beginner_description || w.style_summary, 'bc-ww__lead') +
          blk('Buy this if', w.buy_this_if) +
          blk('If you like', w.if_you_like) +
          blk('Not for', w.not_for) +
          blk('On the price', w.price_context) +
          tagsHtml('Good with, here', w.buon_cibo_pairings, 'bc-ww__tags--dish') +
        '</div>' +
        '<div class="bc-ww__blk bc-ww__blk-col">' +
          scalesHtml(w) +
          tagsHtml('Tastes of', w.tasting_notes || w.flavor_tags) +
          serveHtml(w) +
          blk('On the nose', w.aroma) +
          blk('The finish', w.finish) +
          (origin.length
            ? '<div class="bc-ww__blk"><h4>Where it is from</h4><dl class="bc-ww__specs">' +
              origin.map(function (r) {
                return '<div><dt>' + r[0] + '</dt><dd>' + esc(r[1]) + '</dd></div>';
              }).join('') + '</dl></div>'
            : '') +
          blk('How it is made', w.winemaking_method) +
        '</div>' +
      '</div>';
  }

  function openWine(w, from) {
    if (!w) return;
    opener = from || null;
    sheetIn.innerHTML = sheetHtml(w);
    if (typeof sheet.showModal === 'function') sheet.showModal();
    else sheet.setAttribute('open', '');
    var close = sheetIn.querySelector('[data-bc-ww-close]');
    if (close) close.focus();
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('wine', w.wall_number || w.wine_id);
      history.replaceState(null, '', url);
    } catch (e) { /* a URL the browser will not parse is not worth failing over */ }
  }

  function closed() {
    sheetIn.innerHTML = '';
    if (opener && document.contains(opener)) opener.focus();
    opener = null;
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('wine');
      history.replaceState(null, '', url);
    } catch (e) { /* as above */ }
  }

  /* ---------------------------------------------------------------- rendering */

  function matches(w) {
    if (type !== 'all' && w.category !== type) return false;
    if (query && (w._s || '').indexOf(query) === -1) return false;
    return true;
  }

  function paint() {
    var shown = WINES.filter(matches);
    grid.innerHTML = shown.length
      ? shown.map(function (w) { return cardHtml(certify(w)); }).join('')
      : '<p class="bc-ww__empty">Nothing on the wall matches that. Clear the search, ' +
        'or ask whoever is behind the counter.</p>';

    var tail = (type === 'all' && !query) ? ' · the whole wall, in wall order' : '';
    countEl.textContent = shown.length + (shown.length === 1 ? ' bottle' : ' bottles') + tail;

    Array.prototype.forEach.call(chips.children, function (c) {
      c.setAttribute('aria-pressed', String(c.getAttribute('data-t') === type));
    });
  }

  function paintChips() {
    chips.innerHTML = TYPES.map(function (t) {
      var n = t[0] === 'all'
        ? WINES.length
        : WINES.filter(function (w) { return w.category === t[0]; }).length;
      if (!n) return '';
      return '<button class="bc-page__chip" type="button" data-t="' + t[0] + '" ' +
        'aria-pressed="' + (t[0] === type) + '">' + t[1] + ' <span aria-hidden="true">' + n +
        '</span><span class="visually-hidden">, ' + n + ' bottles</span></button>';
    }).join('');
  }

  function paintBars() {
    if (!bars) return;
    var by = {};
    WINES.forEach(function (w) {
      var k = w.country || 'Elsewhere';
      by[k] = (by[k] || 0) + 1;
    });
    var rows = Object.keys(by).map(function (k) { return [k, by[k]]; })
      .sort(function (a, b) { return b[1] - a[1]; });
    var top = rows[0][1];
    bars.innerHTML = rows.map(function (r) {
      return '<div class="bc-ww__bar"><b>' + esc(r[0]) + '</b>' +
        '<span style="width:' + Math.round((r[1] / top) * 100) + '%"></span>' +
        '<u>' + r[1] + '</u></div>';
    }).join('');
  }

  /* ---------------------------------------------------------------- wiring */

  function byWall(n) {
    for (var i = 0; i < WINES.length; i++) {
      if (String(WINES[i].wall_number) === String(n)) return WINES[i];
    }
    return null;
  }

  function onSearch() {
    var raw = input.value.trim();
    if (!raw) {
      query = '';
      hint.textContent = input.getAttribute('data-hint-default') || '';
      paint();
      return;
    }
    if (/^\d+$/.test(raw)) {
      var hit = byWall(raw);
      if (hit) {
        query = '';
        type = 'all';
        hint.textContent = hit.display_name;
        paint();
        openWine(certify(hit), input);
      } else {
        query = '';
        hint.textContent = 'No wine at number ' + raw + ' on the wall.';
        paint();
      }
      return;
    }
    query = raw.toLowerCase();
    hint.textContent = 'Showing bottles matching “' + raw + '”.';
    paint();
  }

  chips.addEventListener('click', function (e) {
    var b = e.target.closest('[data-t]');
    if (!b) return;
    type = b.getAttribute('data-t');
    paint();
  });

  grid.addEventListener('click', function (e) {
    var b = e.target.closest('[data-id]');
    if (!b) return;
    var id = b.getAttribute('data-id');
    for (var i = 0; i < WINES.length; i++) {
      if (WINES[i].wine_id === id) { openWine(certify(WINES[i]), b); return; }
    }
  });

  sheet.addEventListener('click', function (e) {
    /* a click on the backdrop lands on the dialog itself, never on its contents */
    if (e.target === sheet || e.target.closest('[data-bc-ww-close]')) sheet.close();
  });
  sheet.addEventListener('close', closed);

  if (input) {
    input.addEventListener('input', onSearch);
    input.addEventListener('search', onSearch);
  }

  /* ---------------------------------------------------------------- load */

  fetch(DATA_URL, { credentials: 'same-origin' })
    .then(function (r) {
      if (!r.ok) throw new Error('wine data ' + r.status);
      return r.json();
    })
    .then(function (payload) {
      WINES = payload.wines || [];
      paintChips();
      paintBars();
      paint();
      root.setAttribute('data-state', 'ready');

      /* ?wine=57 opens a bottle directly - design system section 6 */
      try {
        var want = new URL(window.location.href).searchParams.get('wine');
        if (want) {
          var hit = byWall(want) || WINES.filter(function (w) { return w.wine_id === want; })[0];
          if (hit) openWine(certify(hit), null);
        }
      } catch (e) { /* no deep link, no problem */ }
    })
    .catch(function () {
      root.setAttribute('data-state', 'error');
      grid.innerHTML = '<p class="bc-ww__empty">The wine list did not load. Refresh the page, ' +
        'or call the shop on <a href="tel:8582656702">(858) 265-6702</a> and we will read it ' +
        'to you.</p>';
      countEl.textContent = '';
    });
})();
